import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { ReasonDialog } from "@/features/invoicing/components/ReasonDialog";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  useApproveInputDiscard,
  useApproveAndWriteOff,
  useInputDiscardPending,
} from "../hooks/useInputDiscard";
import type { InputDiscardRequestDto } from "../services/inputDiscardApi";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** Modal that collects optional notes then calls Approve & Write Off */
const ApproveWriteOffModal = ({
  item,
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: {
  item: InputDiscardRequestDto | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: (notes: string) => void;
  isSubmitting: boolean;
}) => {
  const [notes, setNotes] = useState("");

  const handleClose = () => {
    setNotes("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Approve &amp; Write Off Stock</DialogTitle>
          <DialogDescription>
            This will permanently deduct{" "}
            <strong>
              {item?.quantityToDiscard} {item?.unit}
            </strong>{" "}
            of <strong>{item?.inputName}</strong> (batch {item?.batchNumber}) from store inventory.
            {item?.customerName && ` Customer seed — ${item.customerName}.`}
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor="approvalNotes">Notes (optional)</FieldLabel>
          <Textarea
            id="approvalNotes"
            rows={3}
            placeholder="Any notes for the record..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
          />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => onConfirm(notes)} disabled={isSubmitting}>
            {isSubmitting ? "Processing…" : "Approve & Write Off"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const InputDiscardApprovalsPage = () => {
  const notify = useNotification();
  const pendingQuery = useInputDiscardPending();
  const approveMutation = useApproveInputDiscard();
  const approveAndWriteOffMutation = useApproveAndWriteOff();

  const [selected, setSelected] = useState<InputDiscardRequestDto | null>(null);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const openWriteOff = (item: InputDiscardRequestDto) => {
    setSelected(item);
    setWriteOffOpen(true);
  };

  const openReject = (item: InputDiscardRequestDto) => {
    setSelected(item);
    setRejectOpen(true);
  };

  const handleWriteOff = (notes: string) => {
    if (!selected) return;
    approveAndWriteOffMutation.mutate(
      { id: selected.id, notes: notes || undefined },
      {
        onSuccess: () => {
          notify.success(
            `Written off: ${selected.quantityToDiscard} ${selected.unit} of ${selected.inputName}`
          );
          setWriteOffOpen(false);
          setSelected(null);
        },
        onError: () => notify.error("Failed to process discard"),
      }
    );
  };

  const handleReject = (reason: string) => {
    if (!selected) return;
    approveMutation.mutate(
      {
        id: selected.id,
        dto: { discardRequestId: selected.id, isApproved: false, approvalNotes: reason },
      },
      {
        onSuccess: () => {
          notify.success("Discard request rejected");
          setRejectOpen(false);
          setSelected(null);
        },
        onError: () => notify.error("Failed to reject discard request"),
      }
    );
  };

  const pendingRows = pendingQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Input Discard Approvals</h1>
        <p className="mt-1 text-muted-foreground">
          Review and action pending discard requests for expired or damaged input / seed inventory.
        </p>
        <div className="mt-2">
          <ApproversInfo permission="discard_requests.approve" />
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Pending Approval</CardTitle>
            {pendingRows.length > 0 && (
              <Badge className="border-orange-400 bg-orange-100 text-orange-700">
                {pendingRows.length}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pendingQuery.isLoading && (
            <p className="py-8 text-center text-muted-foreground">Loading...</p>
          )}
          {!pendingQuery.isLoading && pendingRows.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              No pending discard requests.
            </p>
          )}
          {pendingRows.length > 0 && (
            <div className="space-y-3">
              {pendingRows.map((item) => (
                <div key={item.id} className="rounded-lg border bg-card p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-base">{item.inputName}</p>
                      <p className="text-sm text-muted-foreground">Batch {item.batchNumber}</p>
                      {item.customerName && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Customer seed — {item.customerName}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="shrink-0">{item.reason}</Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div>
                      <span className="text-muted-foreground">Quantity: </span>
                      <span className="font-semibold">{item.quantityToDiscard} {item.unit}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Requested by: </span>
                      <span>{item.requestedByUserName}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Requested: </span>
                      <span>{formatDate(item.requestedAt)}</span>
                    </div>
                  </div>

                  <div className="rounded-md bg-muted/50 border px-3 py-2 text-sm">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">
                      Reason details
                    </p>
                    <p className="whitespace-pre-wrap">{item.reasonDetails || "—"}</p>
                  </div>

                  <div className="flex justify-end gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => openWriteOff(item)}
                      disabled={approveAndWriteOffMutation.isPending}
                    >
                      Approve &amp; Write Off
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => openReject(item)}
                      disabled={approveMutation.isPending}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ApproveWriteOffModal
        item={selected}
        open={writeOffOpen}
        onOpenChange={setWriteOffOpen}
        onConfirm={handleWriteOff}
        isSubmitting={approveAndWriteOffMutation.isPending}
      />

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject Discard Request"
        description="Provide a reason. The requestor will be able to see this."
        reasonLabel="Rejection Reason"
        reasonPlaceholder="e.g. Batch still usable — please re-inspect"
        submitLabel="Reject"
        isSubmitting={approveMutation.isPending}
        onSubmit={handleReject}
      />
    </div>
  );
};
